import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { useD2DSuccessStatistics } from "@/hooks/useD2DSuccessStatistics";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { TrendingUp, TrendingDown, Award, Activity } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface D2DSuccessStatisticsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function D2DSuccessStatistics({ open, onOpenChange }: D2DSuccessStatisticsProps) {
  const { user } = useAuth();
  const { data, isLoading } = useD2DSuccessStatistics(user?.id || '');

  const getBarColor = (successRate: number) => {
    if (successRate >= 70) return 'hsl(var(--success))';
    if (successRate >= 50) return 'hsl(var(--warning))';
    return 'hsl(var(--destructive))';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Erfolgsquoten nach Zeitfenster
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-[400px] w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Award className="h-4 w-4 text-success" />
                    Bestes Zeitfenster
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {data?.bestTimeSlot?.totalVisits ? (
                    <>
                      <div className="text-2xl font-bold text-success">
                        {data.bestTimeSlot.successRate}%
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {data.bestTimeSlot.timeSlot}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {data.bestTimeSlot.successfulVisits} von {data.bestTimeSlot.totalVisits} Besuchen
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">Keine Daten</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <TrendingDown className="h-4 w-4 text-destructive" />
                    Schlechtestes Zeitfenster
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {data?.worstTimeSlot?.totalVisits ? (
                    <>
                      <div className="text-2xl font-bold text-destructive">
                        {data.worstTimeSlot.successRate}%
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {data.worstTimeSlot.timeSlot}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {data.worstTimeSlot.successfulVisits} von {data.worstTimeSlot.totalVisits} Besuchen
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">Keine Daten</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Activity className="h-4 w-4 text-primary" />
                    Gesamt analysiert
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-primary">
                    {data?.totalVisitsAnalyzed || 0}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Gesamtanzahl Besuche
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Erfolgsquote pro Zeitfenster</CardTitle>
                <CardDescription>
                  Prozentuale Erfolgsquote basierend auf WIN/HOT/KT Tags
                </CardDescription>
              </CardHeader>
              <CardContent>
                {data?.statistics && data.statistics.some(s => s.totalVisits > 0) ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={data.statistics.filter(s => s.totalVisits > 0)}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="timeSlot" 
                        angle={-45}
                        textAnchor="end"
                        height={120}
                        className="text-xs"
                      />
                      <YAxis 
                        label={{ value: 'Erfolgsquote (%)', angle: -90, position: 'insideLeft' }}
                        domain={[0, 100]}
                      />
                      <Tooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload[0]) {
                            const data = payload[0].payload;
                            return (
                              <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
                                <p className="font-semibold mb-2">{data.timeSlot}</p>
                                <div className="space-y-1 text-sm">
                                  <p className="text-success">✓ Erfolgreich: {data.successfulVisits}</p>
                                  <p className="text-destructive">✗ Nicht erfolgreich: {data.failedVisits}</p>
                                  <p className="font-medium">Erfolgsquote: {data.successRate}%</p>
                                  <p className="text-muted-foreground">Gesamt: {data.totalVisits} Besuche</p>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar dataKey="successRate" radius={[8, 8, 0, 0]}>
                        {data.statistics.filter(s => s.totalVisits > 0).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getBarColor(entry.successRate)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[400px] flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Keine Besuchsdaten verfügbar</p>
                      <p className="text-sm mt-2">
                        Besuchen Sie Kunden und markieren Sie die Ergebnisse mit Tags,
                        um Statistiken zu generieren.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Detailed Table */}
            {data?.statistics && data.statistics.some(s => s.totalVisits > 0) && (
              <Card>
                <CardHeader>
                  <CardTitle>Detaillierte Übersicht</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-4">Zeitfenster</th>
                          <th className="text-right py-2 px-4">Erfolgsquote</th>
                          <th className="text-right py-2 px-4">Erfolgreich</th>
                          <th className="text-right py-2 px-4">Nicht erfolgreich</th>
                          <th className="text-right py-2 px-4">Gesamt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.statistics
                          .filter(s => s.totalVisits > 0)
                          .map((stat, index) => (
                            <tr key={index} className="border-b hover:bg-muted/50">
                              <td className="py-2 px-4">{stat.timeSlot}</td>
                              <td className="text-right py-2 px-4 font-medium">
                                <span className={
                                  stat.successRate >= 70 ? 'text-success' :
                                  stat.successRate >= 50 ? 'text-warning' :
                                  'text-destructive'
                                }>
                                  {stat.successRate}%
                                </span>
                              </td>
                              <td className="text-right py-2 px-4 text-success">
                                {stat.successfulVisits}
                              </td>
                              <td className="text-right py-2 px-4 text-destructive">
                                {stat.failedVisits}
                              </td>
                              <td className="text-right py-2 px-4 font-medium">
                                {stat.totalVisits}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
